/* eslint-disable @typescript-eslint/no-use-before-define */
import { useMemo } from 'react';
import * as React from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { rgba } from 'polished';
import styled, { useTheme } from 'styled-components';

import useMediaQuery from '@/utils/useMediaQuery';

import { ReactComponent as MarkBuildAnything } from '../../images/illustrations/marks/mark-build-anything.svg';
import { INTERNAL_LINKS } from '../../utils';
import { Button, GradientHeader, Text } from '../design-system';
import { ToutWrapper } from '../design-system/ToutWrapper';
import { GRID_SIZE } from '../global/PaperGrid';

import {
  BuildAnythingCard,
  type BuildAnythingCardType
} from './BuildAnythingCard';

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  margin: 0 auto;
`;

const Top = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 640px;
  padding: 0 20px;
  position: relative;
  z-index: 1;
`;

const Icon = styled.div`
  margin-bottom: 10px;
`;

const Title = styled(GradientHeader).attrs({ renderAs: 'h1' })`
  padding: 0 20px 5px;

  && {
    margin-bottom: 10px;
  }
`;

const Description = styled(Text).attrs({ size: 'extra-large' })`
  font-weight: 300;
  text-align: center;
  max-width: 470px;
  margin-bottom: 20px;
`;

const Action = styled.div`
  margin-top: 15px;
`;

const Cards = styled.div`
  margin: -${GRID_SIZE * 4}px auto 0;
  position: relative;
  z-index: 0;
  max-width: 1800px;
  min-width: 0;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    margin-top: 0;
  }
`;

const CardsColumn = styled.div<{ $offset: number }>`
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
  position: relative;
  padding-top: ${({ $offset }) => `${$offset * GRID_SIZE}px`};

  &::before,
  &::after {
    content: '';
    display: block;
    width: 0;
    border-left: 1px dashed
      ${({ theme }) => rgba(theme.borderColor.DEFAULT, 0.15)};
    position: absolute;
    top: -400px;
    bottom: -200px;
  }

  &::before {
    left: -1px;
  }

  &::after {
    right: -1px;
  }
`;

const CardsInner = styled.div`
  display: flex;
  gap: 20px;
  margin: 0 -${GRID_SIZE}px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    margin: 0;
    padding: 60px ${GRID_SIZE}px;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x mandatory;
    scroll-padding: 0 ${GRID_SIZE}px;

    ${CardsColumn} {
      flex: none;
      width: calc(100vw - ${GRID_SIZE * 3}px);
      scroll-snap-align: start;
    }
  }
`;

interface UseCaseCategoryData {
  allContentfulUseCaseCategory: {
    nodes: BuildAnythingCardType[];
  };
}

export const BuildAnythingTout: React.ComponentType = React.memo(() => {
  const theme = useTheme();
  const showThreeColumns = useMediaQuery(theme.mediaQueryString.LARGE, true);
  const showTwoColumns = useMediaQuery(theme.mediaQueryString.MEDIUM);
  const showOneColumn = useMediaQuery(theme.mediaQueryString.SMALL);

  const data: UseCaseCategoryData = useStaticQuery(pageQuery);
  const { nodes } = data.allContentfulUseCaseCategory;
  const dataClusteringInfo = nodes.find(o => o.slug === 'data-clustering');
  const dataModelingInfo = nodes.find(o => o.slug === 'data-modeling');
  const sentimentAnalysisInfo = nodes.find(
    o => o.slug === 'sentiment-analysis'
  );
  const timeSeriesInfo = nodes.find(o => o.slug === 'time-series');
  const kpiDashboardsInfo = nodes.find(o => o.slug === 'kpi-dashboards');
  const nlpInfo = nodes.find(o => o.slug === 'natural-language-processing');
  const exploratoryAnalysisInfo = nodes.find(
    o => o.slug === 'exploratory-analysis'
  );
  const dataVisualizationInfo = nodes.find(
    o => o.slug === 'data-visualization'
  );

  const content = useMemo(() => {
    if (showOneColumn) {
      return (
        <>
          <CardsColumn $offset={0}>
            {dataClusteringInfo && (
              <BuildAnythingCard {...dataClusteringInfo} />
            )}
            {dataModelingInfo && <BuildAnythingCard {...dataModelingInfo} />}
          </CardsColumn>
          <CardsColumn $offset={0}>
            {sentimentAnalysisInfo && (
              <BuildAnythingCard {...sentimentAnalysisInfo} />
            )}
            {timeSeriesInfo && <BuildAnythingCard {...timeSeriesInfo} />}
          </CardsColumn>
          <CardsColumn $offset={0}>
            {kpiDashboardsInfo && <BuildAnythingCard {...kpiDashboardsInfo} />}
            {nlpInfo && <BuildAnythingCard {...nlpInfo} />}
          </CardsColumn>
          <CardsColumn $offset={0}>
            {exploratoryAnalysisInfo && (
              <BuildAnythingCard {...exploratoryAnalysisInfo} />
            )}
            {dataVisualizationInfo && (
              <BuildAnythingCard {...dataVisualizationInfo} />
            )}
          </CardsColumn>
        </>
      );
    }

    if (showTwoColumns) {
      return (
        <>
          <CardsColumn $offset={6}>
            {dataClusteringInfo && (
              <BuildAnythingCard {...dataClusteringInfo} />
            )}
            {dataModelingInfo && <BuildAnythingCard {...dataModelingInfo} />}
            {kpiDashboardsInfo && <BuildAnythingCard {...kpiDashboardsInfo} />}
            {nlpInfo && <BuildAnythingCard {...nlpInfo} />}
          </CardsColumn>
          <CardsColumn $offset={7}>
            {sentimentAnalysisInfo && (
              <BuildAnythingCard {...sentimentAnalysisInfo} />
            )}
            {timeSeriesInfo && <BuildAnythingCard {...timeSeriesInfo} />}
            {exploratoryAnalysisInfo && (
              <BuildAnythingCard {...exploratoryAnalysisInfo} />
            )}
            {dataVisualizationInfo && (
              <BuildAnythingCard {...dataVisualizationInfo} />
            )}
          </CardsColumn>
        </>
      );
    }

    if (showThreeColumns) {
      return (
        <>
          <CardsColumn $offset={3}>
            {dataClusteringInfo && (
              <BuildAnythingCard {...dataClusteringInfo} />
            )}
            {dataModelingInfo && <BuildAnythingCard {...dataModelingInfo} />}
            {exploratoryAnalysisInfo && (
              <BuildAnythingCard {...exploratoryAnalysisInfo} />
            )}
          </CardsColumn>
          <CardsColumn $offset={8}>
            {sentimentAnalysisInfo && (
              <BuildAnythingCard {...sentimentAnalysisInfo} />
            )}
            {timeSeriesInfo && <BuildAnythingCard {...timeSeriesInfo} />}
          </CardsColumn>
          <CardsColumn $offset={5}>
            {kpiDashboardsInfo && <BuildAnythingCard {...kpiDashboardsInfo} />}
            {nlpInfo && <BuildAnythingCard {...nlpInfo} />}
            {dataVisualizationInfo && (
              <BuildAnythingCard {...dataVisualizationInfo} />
            )}
          </CardsColumn>
        </>
      );
    }

    return (
      <>
        <CardsColumn $offset={0}>
          {dataClusteringInfo && <BuildAnythingCard {...dataClusteringInfo} />}
          {dataModelingInfo && <BuildAnythingCard {...dataModelingInfo} />}
        </CardsColumn>
        <CardsColumn $offset={8}>
          {sentimentAnalysisInfo && (
            <BuildAnythingCard {...sentimentAnalysisInfo} />
          )}
          {timeSeriesInfo && <BuildAnythingCard {...timeSeriesInfo} />}
        </CardsColumn>
        <CardsColumn $offset={6}>
          {kpiDashboardsInfo && <BuildAnythingCard {...kpiDashboardsInfo} />}
          {nlpInfo && <BuildAnythingCard {...nlpInfo} />}
        </CardsColumn>
        <CardsColumn $offset={1}>
          {exploratoryAnalysisInfo && (
            <BuildAnythingCard {...exploratoryAnalysisInfo} />
          )}
          {dataVisualizationInfo && (
            <BuildAnythingCard {...dataVisualizationInfo} />
          )}
        </CardsColumn>
      </>
    );
  }, [
    dataClusteringInfo,
    dataModelingInfo,
    dataVisualizationInfo,
    exploratoryAnalysisInfo,
    kpiDashboardsInfo,
    nlpInfo,
    sentimentAnalysisInfo,
    showOneColumn,
    showThreeColumns,
    showTwoColumns,
    timeSeriesInfo
  ]);

  return (
    <ToutWrapper>
      <Inner>
        <Top>
          <Icon>
            <MarkBuildAnything />
          </Icon>
          <Title renderAs="h1">Build anything with data</Title>
          <Description>
            Learn how to do almost anything in Hex, from complex ML forecasting
            to critical business dashboards.
          </Description>
          <Action>
            <Button
              iconPos="right"
              link={INTERNAL_LINKS.Templates}
              size="large"
              stylez="loud"
              text="View all templates"
            />
          </Action>
        </Top>
      </Inner>
      <Cards>
        <CardsInner>{content}</CardsInner>
      </Cards>
    </ToutWrapper>
  );
});

const pageQuery = graphql`
  query {
    allContentfulUseCaseCategory(
      filter: {
        slug: {
          in: [
            "data-clustering"
            "data-modeling"
            "data-visualization"
            "exploratory-analysis"
            "natural-language-processing"
            "kpi-dashboards"
            "sentiment-analysis"
            "time-series"
          ]
        }
      }
    ) {
      nodes {
        slug
        title
        description {
          description
        }
        use_case {
          name
          slug
          category {
            title
          }
          seoMetaDescription
          gridImage {
            title
            gatsbyImageData(placeholder: BLURRED, formats: [AUTO, WEBP])
          }
        }
      }
    }
  }
`;
